/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormControl, FormHelperText, OutlinedInput } from '@mui/material';

import { Namespaces } from '../../../i18n';
import { useFormStyles } from '../../../styles/form';
import {
  ApiValidationGetFieldError,
  ApiValidationHasFieldError,
  ApiValidationResetFieldError,
} from '../../../utils/useApiValidation';
import { useValidationTranslation } from '../../../utils/useValidationTranslation';
import { createRecoveryPasswordFormStyles } from '../RecoveryPasswordPage.styles';
import { RecoveryPasswordFormValues } from '../RecoveryPasswordPage.types';

export type RecoveryPasswordFormProps = FormikProps<RecoveryPasswordFormValues> & {
  isFetching: boolean;
  resetApiError: ApiValidationResetFieldError;
  hasFieldApiError: ApiValidationHasFieldError;
  getFieldApiError: ApiValidationGetFieldError;
};

export const RecoveryPasswordForm = memo<RecoveryPasswordFormProps>(props => {
  const { t } = useTranslation(Namespaces.Login);
  const {
    handleSubmit,
    values: { password, confirmPassword },
    isFetching,
    handleChange,
    setFieldTouched,
    touched,
    errors,
    resetApiError,
    hasFieldApiError,
    getFieldApiError,
  } = props;
  const classes = createRecoveryPasswordFormStyles();
  const formClasses = useFormStyles();
  const { vtr } = useValidationTranslation();

  const hasError = (val: keyof RecoveryPasswordFormValues): boolean => {
    return !!touched[val] && !!(errors[val] || hasFieldApiError(val));
  };

  const getError = (val: keyof RecoveryPasswordFormValues): string => {
    const error = errors[val] || getFieldApiError(val);
    if (error) {
      return error;
    }
    return '';
  };

  const change = (name: keyof RecoveryPasswordFormValues, e: ChangeEvent) => {
    e.persist();
    handleChange(e);
    resetApiError(name);
    void setFieldTouched(name, true, false);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className={classes.root}>
      <FormControl className={cx(formClasses.formControl, formClasses.widthFull)} error={hasError('password')}>
        <OutlinedInput
          id="password"
          name="password"
          placeholder={t('page.recovery_password.new_password')}
          error={hasError('password')}
          fullWidth
          type="password"
          value={password}
          onChange={change.bind(null, 'password')}
          disabled={isFetching}
        />
        {hasError('password') && (
          <FormHelperText>
            {vtr(getError('password'), {
              field: t('page.recovery_password.new_password'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl className={cx(formClasses.formControl, formClasses.widthFull)} error={hasError('confirmPassword')}>
        <OutlinedInput
          id="confirmPassword"
          name="confirmPassword"
          placeholder={t('page.recovery_password.confirm_password')}
          error={hasError('confirmPassword')}
          fullWidth
          type="password"
          value={confirmPassword}
          onChange={change.bind(null, 'confirmPassword')}
          disabled={isFetching}
        />
        {hasError('confirmPassword') && (
          <FormHelperText>
            {vtr(getError('confirmPassword'), {
              field: t('page.recovery_password.confirm_password'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <div className={classes.formLine}>
        <Button type="submit" color="primary" variant="contained" size="small" disabled={isFetching}>
          {t('page.recovery_password.submit_button')}
        </Button>
      </div>
    </form>
  );
});
